import type { AxiosInstance } from 'axios';

import { useAuthStore } from '@/features/auth/auth.store';
import { readViewAs } from '@/features/auth/view-as';
import { useCompanyStore } from '@/stores/company.store';

/** True when a caller named a company itself, rather than leaving it blank. */
const named = (value: unknown) => typeof value === 'string' && value !== '';

/**
 * The company every request should be scoped to.
 *
 * The switcher's own store is the source of truth. It is persisted, but
 * zustand rehydrates it asynchronously, so it is still empty for the first
 * moments after a page load. Every query a screen fires during that gap would
 * otherwise go out unscoped and come back with all tenants in it, which is
 * exactly what the switcher exists to prevent.
 *
 * `user.company` is the mirror the switcher writes on every switch, restored
 * by `protectedLoader` before the first render, so it covers the gap. For
 * everyone else it is simply their own company, which the API would have
 * applied anyway.
 *
 * A view-as session is pinned to its company (the API ignores a client
 * `companyId` for the simulated role anyway), so it wins over the Admin's
 * own switcher choice, which is still there for when the view-as ends.
 */
const scopeCompanyId = () => {
  const viewAsCompany = readViewAs()?.session.companyId;
  if (named(viewAsCompany)) return viewAsCompany;

  const selected = useCompanyStore.getState().company?.id;
  if (named(selected)) return selected;

  const persisted = useAuthStore.getState().user?.company?.id;
  return named(persisted) ? persisted : undefined;
};

/**
 * Endpoints the company is never attached to.
 *
 *  - `/company` is the tenant registry itself, as opposed to a tenant's own
 *    data: how a super Admin lists and opens the companies they switch
 *    between. Narrowing it to one company is meaningless — and the endpoint
 *    does not declare the parameter, so it is dropped server-side regardless.
 *  - `/tax` is the tax settings, which are kept without a company: the list,
 *    a rate's detail and a new or edited rate all go out unscoped, whoever is
 *    selected in the switcher.
 *  - `/user/logs` is read by `userId`, the account the events belong to. The
 *    backend asked for no `companyId` there: a super Admin's switcher company
 *    narrowed the account's own events away and the log came back empty.
 *  - `/administration/invoice` is Eliio's invoices to its clients, read
 *    across every client for the Invoices list's Sent column. Scoped, a super
 *    Admin's switcher company narrowed it to that one client. The dialog
 *    names its client itself, and a send always carries one.
 *
 * Each is matched on a whole path segment on purpose: `/company-config` and
 * `/company-detail` are a tenant's own settings and must stay scoped, which a
 * plain `startsWith('/company')` would quietly break.
 */
const UNSCOPED_PATHS = [
  /^\/company(?:\/|$)/,
  /^\/tax(?:\/|$)/,
  /^\/user\/logs(?:\/|$)/,
  /^\/administration\/invoice(?:\/|$)/,
];

const isUnscoped = (path: string) =>
  UNSCOPED_PATHS.some(pattern => pattern.test(path));

/** The path alone — no origin, no query string. */
const pathOf = (url = '') => {
  const withoutOrigin = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '');
  const withoutQuery = withoutOrigin.split('?')[0];
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
};

/**
 * Attaches the company picked in the sidebar switcher to every booking-API
 * request, so the tenant a super Admin is looking at actually reaches the
 * server.
 *
 * The switcher is hidden from everyone else, so for a normal user this only
 * ever sends the company they already belong to — which is what the API would
 * have scoped them to by itself.
 *
 * Reads go on the query string and writes go in the body, matching how the API
 * accepts the field. An explicit value wins: a caller that already names a
 * company (the client config form, for example) means it. An empty string does
 * not count as one — much of the CMS writes `user?.company?.id ?? ''`, which
 * is "unknown", and a super Admin has no company of their own to fill it.
 *
 * Sending it where an endpoint does not expect it is harmless — the API runs
 * its ValidationPipe with `whitelist: true`, so an undeclared `companyId` is
 * stripped before any handler sees it.
 */
const withCompanyScope = (instance: AxiosInstance) => {
  instance.interceptors.request.use(config => {
    const companyId = scopeCompanyId();
    if (!companyId) return config;

    if (isUnscoped(pathOf(config.url))) return config;

    const method = (config.method ?? 'get').toLowerCase();

    if (method === 'get' || method === 'delete') {
      const params = (config.params ?? {}) as Record<string, unknown>;
      config.params = named(params.companyId)
        ? params
        : { ...params, companyId };
      return config;
    }

    // Only plain JSON bodies. FormData (file uploads) and pre-serialised
    // strings are left exactly as they are.
    const body: unknown = config.data;
    const isPlainObject =
      typeof body === 'object' &&
      body !== null &&
      !Array.isArray(body) &&
      !(body instanceof FormData) &&
      !(body instanceof Blob) &&
      !(body instanceof URLSearchParams);

    if (isPlainObject) {
      const data = body as Record<string, unknown>;
      config.data = named(data.companyId) ? data : { ...data, companyId };
    }

    return config;
  });

  return instance;
};

export default withCompanyScope;
